import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { subscriptionPriceLabel } from "@/lib/pricing";
import { LegalFooter } from "@/components/LegalLinks";

export default function BillingSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking");
  const [checkout, setCheckout] = useState(null);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    pollStatus(sessionId, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const pollStatus = async (sid, attempts) => {
    if (attempts >= 5) {
      setStatus("failed");
      return;
    }
    try {
      const res = await api.getCheckoutStatus(sid);
      if (res.data.plan_set) {
        setCheckout(res.data);
        setStatus("success");
        await refreshUser();
        return;
      } else if (res.data.status === "expired") {
        setStatus("failed");
        return;
      }
      setTimeout(() => pollStatus(sid, attempts + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(sid, attempts + 1), 2000);
    }
  };

  // The price they actually signed up at, and when the free trial (if any) hands over to billing
  const price = subscriptionPriceLabel(checkout);
  const firstCharge = checkout?.trial_end
    ? new Date(checkout.trial_end * 1000).toLocaleDateString(undefined, { month: "long", day: "numeric" })
    : "";
  const billingLine = firstCharge
    ? `${price || "Starts"} after your free trial ends on ${firstCharge}`
    : `${price ? `${price}, ` : ""}auto-renewal`;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="billing-success-page">
      <Card className="max-w-md w-full border-border shadow-sm" data-testid="billing-result-card">
        <CardContent className="pt-8 pb-8 text-center">
          {status === "checking" && (
            <>
              <Loader2 className="w-12 h-12 text-gray-400 mx-auto mb-4 animate-spin" />
              <h2 className="text-xl font-semibold text-gray-900">
                Verifying payment...
              </h2>
              <p className="mt-2 text-sm text-gray-500">Please wait while we confirm your subscription.</p>
            </>
          )}
          {status === "success" && (
            <>
              <CheckCircle className="w-12 h-12 text-sage mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900">
                Welcome to Premium
              </h2>
              <p className="mt-2 text-sm text-gray-500">Your subscription is active. You now have full access to all Premium features.</p>
              <div className="mt-4 bg-muted rounded-lg p-3 text-left space-y-1">
                <p className="text-xs text-gray-500"><span className="font-medium text-gray-700">Plan:</span> CartControl Premium</p>
                <p className="text-xs text-gray-500"><span className="font-medium text-gray-700">Billing:</span> {billingLine}</p>
                <p className="text-xs text-gray-500"><span className="font-medium text-gray-700">Manage:</span> Settings &gt; Manage Billing</p>
              </div>
              <Button
                className="mt-6 bg-navy hover:bg-navy-hover active:bg-navy-active text-white font-medium text-sm h-11 px-8"
                onClick={() => navigate("/dashboard", { replace: true })}
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
              <div className="mt-4">
                <LegalFooter />
              </div>
            </>
          )}
          {status === "failed" && (
            <>
              <XCircle className="w-12 h-12 text-danger mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900">
                Payment Issue
              </h2>
              <p className="mt-2 text-sm text-gray-500">We couldn't verify your payment. Please try again or contact support.</p>
              <div className="mt-6 flex gap-3 justify-center">
                <Button variant="outline" className="border-border text-gray-700" onClick={() => navigate("/pricing")} data-testid="try-again-btn">
                  Try Again
                </Button>
                <Button className="bg-navy hover:bg-navy-hover active:bg-navy-active text-white" onClick={() => navigate("/dashboard")} data-testid="back-dashboard-btn">
                  Dashboard
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
